// Lead magnet registry + per-service placement.
// Rule: a page shows a diagnostic only when a genuinely relevant resource
// exists (approved mapping, 2026-10-16).

export const LEAD_MAGNETS = {
  "pricing-leakage-diagnostic": {
    title: "Pricing Leakage Diagnostic",
    fullTitle: "Pricing Leakage Diagnostic: 20 Checks to Identify Hidden Margin Losses",
    file: "/resources/pricing-leakage-diagnostic.pdf",
    headline: "Where Is Your Profit Leaking?",
    copy: "Identify hidden margin losses across discounts, trade schemes and channel economics with our free 20-point Pricing Leakage Diagnostic.",
    cta: "Get the Free Pricing Diagnostic",
    scope: "Discounts · Trade schemes · Exceptions · Realisation · Channel margins · Promotions · Profitability",
    preview: [
      "Every discount has a documented approval threshold and a named owner.",
      "We know the realised (net) price for our top 20 SKUs or customers.",
      "Post-promotion analysis compares actual versus planned uplift and margin.",
      "Contribution margin is known by product and by customer.",
    ],
  },
  "sales-performance-diagnostic": {
    title: "Sales Performance Diagnostic",
    fullTitle: "Sales Performance Diagnostic: 20 Checks to Identify Revenue Growth Bottlenecks",
    file: "/resources/sales-performance-diagnostic.pdf",
    headline: "What Is Capping Your Sales Growth?",
    copy: "Locate the real bottleneck — productivity, coverage, pipeline, incentives or management rhythm — with our free 20-point Sales Performance Diagnostic.",
    cta: "Get the Free Sales Diagnostic",
    scope: "Productivity · Pipeline · Territory · Conversion · Coverage · Incentives · Process",
    preview: [
      "Revenue per salesperson is measured and trended over time.",
      "Active coverage versus potential is measured for each territory.",
      "Conversion rates are known at each stage of the funnel.",
      "Sales reviews are forward-looking working sessions, not result readings.",
    ],
  },
  "gtm-readiness-checklist": {
    title: "GTM Readiness Checklist",
    fullTitle: "Go-to-Market Readiness: 20 Checks Before Launching a Product or Entering a Market",
    file: "/resources/gtm-readiness-checklist.pdf",
    headline: "Is Your Launch Actually Ready?",
    copy: "Test opportunity, targeting, positioning, pricing, channel and sales readiness before you commit — with our free 20-point GTM Readiness Checklist.",
    cta: "Get the Free GTM Checklist",
    scope: "Opportunity · Segmentation · Positioning · Pricing · Channel · Sales readiness · Demand · Measurement",
    preview: [
      "The market opportunity is sized with explicit, testable assumptions.",
      "Our differentiation is expressed as customer value, not product features.",
      "The channel matches how the target customer actually buys.",
      "Scale-up and kill criteria are agreed before launch.",
    ],
  },
  "marketplace-profitability-diagnostic": {
    title: "Marketplace Profitability Diagnostic",
    fullTitle: "Marketplace Profitability Diagnostic: 20 Checks to Find Where Marketplace Economics Break",
    file: "/resources/marketplace-profitability-diagnostic.pdf",
    headline: "Is Your Marketplace Growth Actually Profitable?",
    copy: "Find where fees, advertising, returns and fulfilment quietly erase marketplace margin — with our free 20-point Marketplace Profitability Diagnostic.",
    cta: "Get the Free Marketplace Diagnostic",
    scope: "Fees · Advertising · Pricing · Returns · Fulfilment · Buy Box · SKU profitability",
    preview: [
      "Contribution margin is known per SKU, per marketplace, after all fees.",
      "Advertising is measured against total sales (TACoS), not only ad-attributed sales.",
      "A governed floor price protects margin on every marketplace.",
      "Loss-making SKUs carry an explicit fix, reprice or delist decision.",
    ],
  },
  "brand-positioning-health-check": {
    title: "Brand Positioning Health Check",
    fullTitle: "Brand Positioning Health Check: 20 Checks to Test Whether Your Brand Still Wins Choice",
    file: "/resources/brand-positioning-health-check.pdf",
    headline: "Does Your Brand Still Win Choice?",
    copy: "Test whether your positioning still earns preference and price premium — or has quietly blurred — with our free 20-point Brand Positioning Health Check.",
    cta: "Get the Free Brand Health Check",
    scope: "Differentiation · Targeting · Preference · Price premium · Portfolio · Consistency",
    preview: [
      "Customers can articulate our difference without prompting.",
      "Positioning is documented and shared — not held in a founder's head.",
      "Price premium versus key competitors is measured over time.",
      "Positioning is formally reviewed at defined intervals as markets shift.",
    ],
  },
  "market-customer-intelligence-health-check": {
    title: "Market & Customer Intelligence Health Check",
    fullTitle: "Market & Customer Intelligence Health Check: 20 Checks to Test Whether Decisions Rest on Evidence",
    file: "/resources/market-customer-intelligence-health-check.pdf",
    headline: "Are Your Growth Decisions Built on Evidence?",
    copy: "Test whether market sizing, customer understanding, competitor tracking and trend sensing actually reach your decisions — with our free 20-point Intelligence Health Check.",
    cta: "Get the Free Intelligence Health Check",
    scope: "Opportunity sizing · Customer insight · Segmentation · Competitors · Decision journeys · Foresight",
    preview: [
      "Key growth decisions are backed by current evidence, not opinions or habit.",
      "We distinguish the headline market from the pool we can actually win.",
      "Competitor moves are tracked systematically, not anecdotally.",
      "Insight findings end in decisions — not presentations that gather dust.",
    ],
  },
};

// Service slug → most relevant existing resource (null = don't show).
export const SERVICE_LEAD_MAGNET = {
  "pricing-revenue-management": "pricing-leakage-diagnostic",
  "product-portfolio-strategy": "pricing-leakage-diagnostic",
  "commercial-excellence-operating-model": "pricing-leakage-diagnostic",
  "sales-transformation": "sales-performance-diagnostic",
  "channel-route-to-market": "sales-performance-diagnostic",
  "go-to-market-market-entry": "gtm-readiness-checklist",
  "amazon-marketplace-growth": "marketplace-profitability-diagnostic",
  "ecommerce-marketplace-growth": "marketplace-profitability-diagnostic",
  "brand-strategy-positioning": "brand-positioning-health-check",
  "brand-architecture-portfolio": "brand-positioning-health-check",
  "market-opportunity-sizing": "market-customer-intelligence-health-check",
  "customer-buyer-insights": "market-customer-intelligence-health-check",
  "segmentation-targeting": "market-customer-intelligence-health-check",
  "competitive-intelligence": "market-customer-intelligence-health-check",
  "customer-decision-journey-intelligence": "market-customer-intelligence-health-check",
  "trends-foresight-growth-opportunities": "market-customer-intelligence-health-check",
};

// Industry slug → most relevant existing resource (all 8 industries mapped;
// each resource genuinely matches the industry's commercial reality).
export const INDUSTRY_LEAD_MAGNET = {
  "fmcg-consumer-products": "sales-performance-diagnostic",
  "consumer-durables-electronics-appliances": "sales-performance-diagnostic",
  "retail-d2c-ecommerce": "marketplace-profitability-diagnostic",
  "manufacturing-b2b": "pricing-leakage-diagnostic",
  "education-edtech": "market-customer-intelligence-health-check",
  "media-entertainment": "brand-positioning-health-check",
  "real-estate": "brand-positioning-health-check",
  "professional-services": "brand-positioning-health-check",
};

// Practice hub → diagnostic offered on the hub itself.
export const PRACTICE_LEAD_MAGNET = {
  "digital-commerce": "marketplace-profitability-diagnostic",
  "market-customer-intelligence": "market-customer-intelligence-health-check",
};
